import React, { createContext, useContext, useEffect, useState } from "react";
import { db } from "../firebase";
import { doc, onSnapshot, setDoc } from "firebase/firestore";

// Create the context
const PriceContext = createContext();

// Provider component
export function PriceProvider({ children }) {
  const [pricePerPageBlackwhite, setPricePerPageBlackwhite] = useState(1);
  const [pricePerPageColor, setPricePerPageColor] = useState(5);
  const [priceMatrix, setPriceMatrix] = useState({
    oneSide: {
      under30: { bw: 1, color: 5 },
      over30: { bw: 1, color: 5 },
    },
    twoSide: {
      under30: { bw: 1, color: 5 },
      over30: { bw: 1, color: 5 },
    },
  });

  useEffect(() => {
    let unsub;
    let timeoutId;
    
    const setupListener = () => {
      try {
        unsub = onSnapshot(
          doc(db, "settings", "main"), 
          (docSnap) => {
            if (docSnap.exists()) {
              const data = docSnap.data();
              setPricePerPageBlackwhite(data.pricePerPageBlackwhite || 1);
              setPricePerPageColor(data.pricePerPageColor || 5);
              if (data.priceMatrix) {
                setPriceMatrix(data.priceMatrix);
              }
            }
          },
          (error) => {
            console.warn('PriceContext listener error:', error);
            // Retry after 5 seconds on error
            timeoutId = setTimeout(() => {
              setupListener();
            }, 5000);
          }
        );
      } catch (error) {
        console.warn('Failed to setup PriceContext listener:', error);
        // Use default values on error
      }
    };
    
    setupListener();
    
    return () => {
      if (unsub) unsub();
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  const setPrices = async (blackwhite, color) => {
    await setDoc(
      doc(db, "settings", "main"),
      { pricePerPageBlackwhite: blackwhite, pricePerPageColor: color },
      { merge: true }
    );
  };

  const savePriceMatrix = async (matrix) => {
    await setDoc(
      doc(db, "settings", "main"),
      { priceMatrix: matrix },
      { merge: true }
    );
  };

  // Calculate price based on paper sides needed
  const calculatePrice = (pageCount, colorType, sideType, useLayout = false, orientation = "2pages", copies = 1) => {
    const normalizedColor = (colorType || "blackwhite").toLowerCase();
    const normalizedSide = (sideType || "single").toLowerCase();
    const rangeKey = pageCount >= 30 ? "over30" : "under30";
    const categoryKey = normalizedSide === "double" ? "twoSide" : "oneSide";
    const colorKey = normalizedColor === "color" ? "color" : "bw";

    const category = priceMatrix?.[categoryKey];
    const range = category?.[rangeKey];
    const pricePerSide = range?.[colorKey];

    if (typeof pricePerSide !== "number") {
      // fallback to legacy prices if matrix not set properly
      const fallbackPrice = normalizedColor === "color" ? pricePerPageColor : pricePerPageBlackwhite;
      return {
        total: fallbackPrice * pageCount * copies,
        sidesNeeded: pageCount,
        pricePerSide: fallbackPrice
      };
    }

    // Calculate pricing based on whether layout option is chosen
    let sidesNeeded;
    let total;
    
    if (!useLayout) {
      // Normal printing: Use original per-page pricing
      sidesNeeded = pageCount;
      total = pricePerSide * pageCount * copies;
    } else {
      // Layout printing: Use per-side pricing
      const pagesPerSide = orientation === "2pages" ? 2 : 4;
      sidesNeeded = Math.ceil(pageCount / pagesPerSide);
      total = pricePerSide * sidesNeeded * copies;
    }

    return {
      total: total,
      sidesNeeded: sidesNeeded,
      pricePerSide: pricePerSide
    };
  };

  // Legacy function for backward compatibility
  const getPerPagePrice = (pageCount, colorType, sideType) => {
    const result = calculatePrice(pageCount, colorType, sideType, false, "2pages", 1);
    return result.total;
  };

  return (
    <PriceContext.Provider
      value={{
        pricePerPageBlackwhite,
        pricePerPageColor,
        priceMatrix,
        getPerPagePrice,
        calculatePrice,
        savePriceMatrix,
        setPrices,
      }}
    >
      {children}
    </PriceContext.Provider>
  );
}

// Custom hook to use the context
export function usePrice() {
  return useContext(PriceContext);
}